import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import Pagination from '../../../components/common/Pagination';

const STATUS_COLORS = { paid: '#198754', pending: '#fd7e14', overdue: '#dc3545', partial: '#6610f2' };

export default function FeesPage() {
  const [fees, setFees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ student_name:'', student_id:'', class_grade:'', fee_type:'Tuition Fee', amount:'', payment_status:'pending', payment_mode:'cash', due_date:'', remarks:'' });
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (filterStatus) params.payment_status = filterStatus;
    const r = await api.get('/education/fees', { params }).catch(() => ({ data: { records: [], total: 0 } }));
    setFees(r.data.records || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  }, [page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/education/fees', form);
      setToast({ show: true, message: 'Fee record created!', type: 'success' });
      setShowForm(false); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      <PageHeader title="Fee Management" icon="bi-cash-coin" subtitle="Manage student fees and payments"
        actions={
          <div className="d-flex gap-2">
            <select className="form-select form-select-sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={{ width: 140 }}>
              <option value="">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s} className="text-capitalize">{s}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}><i className="bi bi-plus-lg me-1"></i>Add Fee</button>
          </div>
        }
      />

      <ModuleHeaderStrip
        icon="bi-cash-coin"
        label="Fee Management"
        chips={[
          { label: 'Total records', value: total },
          { label: 'Filter',        value: filterStatus || 'All' },
        ]}
        variant="success"
      />

      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between"><span>Add Fee Record</span><button className="btn-close" onClick={() => setShowForm(false)}></button></div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-4"><label className="form-label">Student Name *</label><input className="form-control" value={form.student_name} onChange={e => f('student_name', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label">Class / Grade</label><input className="form-control" value={form.class_grade} onChange={e => f('class_grade', e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label">Fee Type</label>
                  <select className="form-select" value={form.fee_type} onChange={e => f('fee_type', e.target.value)}>
                    {['Tuition Fee','Transport Fee','Exam Fee','Library Fee','Lab Fee','Activity Fee','Other'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-2"><label className="form-label">Amount (&#8377;) *</label><input type="number" className="form-control" value={form.amount} onChange={e => f('amount', e.target.value)} required /></div>
                <div className="col-md-3">
                  <label className="form-label">Payment Status</label>
                  <select className="form-select" value={form.payment_status} onChange={e => f('payment_status', e.target.value)}>
                    {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s} className="text-capitalize">{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label">Payment Mode</label>
                  <select className="form-select" value={form.payment_mode} onChange={e => f('payment_mode', e.target.value)}>
                    {['Cash','Online','Cheque','DD','UPI'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label">Due Date</label><input type="date" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} /></div>
                <div className="col-md-9"><label className="form-label">Remarks</label><input className="form-control" value={form.remarks} onChange={e => f('remarks', e.target.value)} /></div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving && <span className="spinner-border spinner-border-sm me-1"></span>}Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead><tr><th>Student</th><th>Class</th><th>Fee Type</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {fees.length === 0 ? <tr><td colSpan={6} className="text-center text-muted py-4">No fee records found</td></tr> : fees.map(fee => {
                  const s = fee.recordData?.payment_status || 'pending';
                  return (
                    <tr key={fee._id}>
                      <td className="fw-medium">{fee.recordData?.student_name}</td>
                      <td>{fee.recordData?.class_grade || '—'}</td>
                      <td><span className="badge bg-light text-dark">{fee.recordData?.fee_type}</span></td>
                      <td className="fw-semibold">&#8377;{Number(fee.recordData?.amount || 0).toLocaleString()}</td>
                      <td><span className="badge" style={{ background: `${STATUS_COLORS[s]}20`, color: STATUS_COLORS[s] }}>{s}</span></td>
                      <td style={{ fontSize: 12 }}>{fee.recordData?.payment_date || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Pagination page={page} totalPages={Math.ceil(total/20)} onPageChange={setPage} total={total} limit={20} />
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
